import { LightDomStyles, OutlineElement } from '@phase2/outline-core';
import { classMap } from 'lit/directives/class-map.js';
import { html, TemplateResult, CSSResultGroup } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import componentStyles from './outline-grid-baseline.css.lit';
import componentStylesLightDom from './baseline-grid-light-dom.css.lit';

/**
 * The Grid component
 * @slot - The default, and only slot for this element.
 */
@customElement('outline-grid-baseline')
export class OutlineGridBaseline extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  lightDomStyles = new LightDomStyles(
    this,
    [componentStylesLightDom],
    this.tagName.toLowerCase()
  );

  /**
   * The background-color.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'background-color',
  })
  backgroundColor: 'white' | 'gray' | 'blue' | 'transparent' = 'transparent';

  /**
   * Whether or not this is a full bleed container.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'content-max-width',
  })
  contentMaxWidth: 'small' | 'medium' | 'none' | 'limit' = 'medium';

  /**
   * How much of a gap should exist between columns? (This affects both the vertical and horizontal column/row gaps.)
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'gap-size',
  })
  gapSize: 'small' | 'medium' | 'extra-small' | 'none' = 'small';

  /**
   * Whether or not this container has top/bottom padding.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'y-padding',
  })
  yPadding: 'small' | 'extra-small' | 'medium' | 'large' | 'none';

  /**
   * Whether or not this container has top/bottom padding.
   */
  @property({
    type: Boolean,
    reflect: true,
    attribute: 'x-padding',
  })
  xPadding: boolean;

  /**
   * Whether or not the grid components should be equal heights.
   */
  @property({
    type: Boolean,
    reflect: true,
    attribute: 'equal-heights',
  })
  equalHeights: boolean;

  /**
   * Whether or not the grid background should have a border.
   */
  @property({
    type: Boolean,
    reflect: true,
    attribute: 'has-border-bottom',
  })
  hasBorderBottom: boolean;

  /**
   * Whether or not the grid background should be an SVG background.
   */
  @property({
    type: Boolean,
    reflect: true,
    attribute: 'background-svg',
  })
  backgroundSvg: boolean;

  render(): TemplateResult {
    const gridClasses = {
      [`background-color-${this.backgroundColor}`]: true,
      'grid-wrapper': true,
      [`gap-${this.gapSize}`]: true,
      [`max-w-${this.contentMaxWidth}`]: true,
      [`y-padding-${this.yPadding}`]: true,
      'background-svg': this.backgroundSvg,
    };

    return html`
      <div class="${classMap(gridClasses)}">
        <slot></slot>
      </div>
    `;
  }
}
